import React, { useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  TextField,
  ClickAwayListener,
  Tooltip,
  IconButton,
  CircularProgress
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DateRange } from "react-date-range";
import Select, { components } from "react-select";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import CollectionReceivedLineChart from "./UserReports/Charts/CollectionReceivedLineChart";
import NetAmountLineChart from "./UserReports/Charts/NetAmountLineChart";
import PendingOrdersBarChart from "./UserReports/Charts/PendingOrdersBarChart";
import SchemesOverviewBarChart from "./UserReports/Charts/SchemesOverviewBarChart";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
// import Fab from "@mui/material/Fab";
// import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import MonthlySalesBarLineChart from "./UserReports/Charts/MonthlySalesBarLineChart";

import ReportsDashboardController from "pages/Controller/ReportsDashboardController";
import { useNavigate } from "react-router-dom";
import SellIcon from "@mui/icons-material/Sell";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

/* ================= COLORS ================= */
const COLORS = {
  primary: "#102A43",
  accent: "#8B6F2F",
  danger: "#8C2F39",
  neutral: "#2F2F2F",
  bg: "#F5F7FA",
  card: "#FFFFFF",
  border: "#E1E6ED",
  textSecondary: "#6B7785"
};

/* ================= KPI TILE ================= */
const KpiTile = ({
  title,
  value,
  subtitle,
  color,
  valueColor,
  valueSuffix,
  valueSuffixColor,
  icon,
  loading,
  disabled
}) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 3,
      height: 110,
      background: disabled ? "#F0F0F0" : COLORS.card,
      borderTop: `6px solid ${disabled ? "#B0B0B0" : color}`,
      boxShadow: disabled
        ? "none"
        : "0 6px 18px rgba(16,42,67,0.08)",
      transition: "all 0.25s ease",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "&:hover": disabled
        ? {}
        : {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 28px rgba(16,42,67,0.18)"
          }
    }}
  >
    {/* ---------- HEADER ---------- */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}
    >
      <Typography fontSize={12} color="#777" fontWeight={800}>
        {title}
      </Typography>

      <Box
        sx={{
          width: 30,
          height: 28,
          borderRadius: "8px",
          backgroundColor: disabled ? "#DDD" : `${color}20`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: disabled ? "#888" : color
        }}
      >
        {icon}
      </Box>
    </Box>

    {/* ---------- VALUE ---------- */}
    {disabled ? (
      <Typography fontSize={14} fontWeight={700} color="#666">
        🚧 Coming Soon
      </Typography>
    ) : loading ? (
      <CircularProgress size={22} />
    ) : (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography
          fontSize={18}
          fontWeight={600}
          lineHeight={1.2}
          color={valueColor || COLORS.primary}
        >
          {value}
        </Typography>
        {valueSuffix && (
          <Typography
            fontSize={14}
            fontWeight={700}
            lineHeight={1.2}
            color={valueSuffixColor || valueColor || COLORS.primary}
          >
            {valueSuffix}
          </Typography>
        )}
      </Box>
    )}

    {/* ---------- SUBTITLE ---------- */}
    <Box
      sx={{
        mt: 0.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}
    >
      <Typography fontSize={10} color="#888">
        {disabled ? "Feature under development" : subtitle}
      </Typography>
    </Box>
  </Paper>
);




/* ================= CHART CARD ================= */
const ChartCard = ({
  title,
  children,
  onViewAll,
  loading,
  disabled
}) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 3,
      height: "100%",
      background: disabled ? "#F3F3F3" : COLORS.card,
      border: `1px solid ${COLORS.border}`,
      transition: "all 0.25s ease",
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
      "&:hover": disabled
        ? {}
        : {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 28px rgba(16,42,67,0.18)"
          }
    }}
  >
    <Box sx={{ position: "relative", height: "100%" }}>
      {/* ---------- HEADER ---------- */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ minHeight: 28 }}
      >
        <Typography fontWeight={700} color={COLORS.primary}>
          {title}
        </Typography>

        {!disabled && onViewAll && (
          <Tooltip title="View All">
          <IconButton
            size="small"
            onClick={onViewAll}
            sx={{
              pointerEvents: "auto",
              color: "#102A43",          // 👈 FORCE visible color
              background: "#F0F4F8",     // 👈 light background so icon pops
              borderRadius: 1,
              p: 0.5,
              "&:hover": {
                background: "#E1E6ED"
              }
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        )}
      </Box>

      <Divider sx={{ my: 0.5 }} />

      {/* ---------- BODY ---------- */}
      {disabled ? (
        // ❌ no chart when disabled
        <Box sx={{ height: 260 }} />
      ) : loading ? (
        <Box
          sx={{
            height: 260,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        children
      )}

      {/* ---------- OVERLAY ---------- */}
      {disabled && (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      background: "rgba(255,255,255,0.65)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      zIndex: 20,
      borderRadius: 3
    }}
  >
    <Typography fontSize={18} fontWeight={700}>
      🚧 Coming Soon
    </Typography>

    <Typography fontSize={12} color="#666" sx={{ mt: 0.5 }}>
      Feature under development
    </Typography>
  </Box>
)}

    </Box>
  </Paper>
);




/* ================= SELECT HELPERS ================= */
const addSelectAllOption = (options) => [
  { label: "Select All", value: "__ALL__", isSelectAll: true },
  ...options
];


const CheckboxOption = (props) => {
  const { label, isSelectAll } = props.data;
  const { isSelected, selectOption, getValue, options } = props;

  const allSelected =
    isSelectAll &&
    getValue().length > 0 &&
    getValue().length === options.filter(o => !o.isSelectAll).length;

  const checked = isSelectAll ? allSelected : isSelected;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectOption(props.data);
  };


  return (
    <button
      type="button"
      onMouseDown={handleClick}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 10px",
        width: "100%",
        background: "#fff",
        border: "none",
        cursor: "pointer"
      }}
    >
      <input type="checkbox" checked={checked} readOnly />
      <span style={{ marginLeft: 8, color: "black" }}>{label}</span>
    </button>
  );
};

const NoMultiValue = () => null;

/* ================= MAIN ================= */
const ReportsDashboard = () => {
  const {
    zoneOptions,
    customerOptions,
    filters,
    handleFilterChange,
    handleCustomerSearch,
    loadAllCustomers,
    loadAllZones,
    dashboardData,
    isFromApp,
    loadingDashboard
  } = ReportsDashboardController();

  // Memoize so react-select menu doesn't "refresh/reset" on every render
  const zoneSelectOptions = useMemo(
    () => addSelectAllOption(zoneOptions),
    [zoneOptions]
  );
  const customerSelectOptions = useMemo(
    () => addSelectAllOption(customerOptions),
    [customerOptions]
  );

  const [showCalendar, setShowCalendar] = useState(false);
  const [range, setRange] = useState([
    { startDate: null, endDate: null, key: "selection" }
  ]);

  const [isZoneOpen, setIsZoneOpen] = useState(false);
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [zoneSearchText, setZoneSearchText] = useState("");
  const [customerSearchText, setCustomerSearchText] = useState("");

  // Clear icon should only clear typed text (not selections/options)
  const makeClearTextOnlyIndicator = (setText) =>
    function ClearTextOnlyIndicator(props) {
      return (
        <components.ClearIndicator
          {...props}
          innerProps={{
            ...props.innerProps,
            onMouseDown: (e) => {
              e.preventDefault();
              e.stopPropagation();
              setText("");
            }
          }}
        />
      );
    };

  const ZoneClearIndicator = useMemo(
    () => makeClearTextOnlyIndicator(setZoneSearchText),
    []
  );
  const CustomerClearIndicator = useMemo(
    () =>
      function ClearTextOnlyIndicator(props) {
        return (
          <components.ClearIndicator
            {...props}
            innerProps={{
              ...props.innerProps,
              onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Clear only the search text, keep selections, but repopulate full list
                setCustomerSearchText("");
                loadAllCustomers();
              }
            }}
          />
        );
      },
    [loadAllCustomers]
  );

  const navigate = useNavigate();

  /* ================= HANDLERS ================= */

  /* ================= SELECT Z-INDEX FIX ================= */
  const selectPortalStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    control: (base) => ({
      ...base,
      minHeight: 44,
      height: 44
    }),
    valueContainer: (base) => ({
      ...base,
      height: 44,
      padding: "0 8px",
      overflow: "hidden"
    }),
    placeholder: (base) => ({
      ...base,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      color: "#000",
      fontWeight: 500
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 44
    })
  };
  


  const handleZoneChange = (selected, actionMeta) => {
    // Clear (cross icon): restore full list, keep current selections
    if (actionMeta?.action === "clear") {
      loadAllZones();
      return;
    }

    if (!selected) return handleFilterChange("zone")([]);

    if (selected.some(o => o.value === "__ALL__")) {
      const allSelected = filters.zone.length === zoneOptions.length;
      return handleFilterChange("zone")(allSelected ? [] : zoneOptions);
    }

    handleFilterChange("zone")(selected);
  };

  const handleCustomerChange = (selected, actionMeta) => {
    // Clear (cross icon): restore full list + clear selection
    if (actionMeta?.action === "clear") {
      loadAllCustomers();
      return;
    }

    if (!selected) return handleFilterChange("customer")([]);

    if (selected.some(o => o.value === "__ALL__")) {
      const allSelected = filters.customer.length === customerOptions.length;
      return handleFilterChange("customer")(allSelected ? [] : customerOptions);
    }

    handleFilterChange("customer")(selected);
  };

  /* ================= EXCLUDED LOGIC ================= */

  /* ---------- CUSTOMER ---------- */
  const excludedCustomers = customerOptions.filter(
    c => !filters.customer.some(s => s.value === c.value)
  );

  const selectedCustomerCount = filters.customer.length;
  const customerExcludedCount = excludedCustomers.length;
  const customerExcludedNames = excludedCustomers.map(c => c.label);
  const customerExcludedText = customerExcludedNames.join(", ");

  const customerPlaceholder = isCustomerOpen
    ? `CUSTOMER${selectedCustomerCount ? ` selected (${selectedCustomerCount})` : ""}`
    : customerExcludedCount
      ? `CUSTOMER: Excluded: ${customerExcludedText.length > 45
        ? customerExcludedText.slice(0, 45) + "..."
        : customerExcludedText
      }`
      : "CUSTOMER";

  const customerTooltip =
    customerExcludedCount > 0
      ? `Excluded:\n${customerExcludedNames.join("\n")}`
      : "";


  /* ---------- ZONE ---------- */
  const excludedZones = zoneOptions.filter(
    z => !filters.zone.some(s => s.value === z.value)
  );

  const selectedZoneCount = filters.zone.length;
  const zoneExcludedCount = excludedZones.length;
  const zoneExcludedNames = excludedZones.map(z => z.label);
  const zoneExcludedText = zoneExcludedNames.join(", ");

  const zonePlaceholder = isZoneOpen
    ? `ZONE${selectedZoneCount ? ` selected (${selectedZoneCount})` : ""}`
    : zoneExcludedCount
      ? `ZONE: Excluded: ${zoneExcludedText.length > 45
        ? zoneExcludedText.slice(0, 45) + "..."
        : zoneExcludedText
      }`
      : "ZONE";

  const zoneTooltip =
    zoneExcludedCount > 0
      ? `Excluded:\n${zoneExcludedNames.join("\n")}`
      : "";


  /* ================= DERIVED DATA ================= */
  const collectionChart = dashboardData?.collectionMonthChart || [];
  const outstandingChart = dashboardData?.outstandingMonthChart || [];
  const pendingChart = dashboardData?.pendingMonthChart || [];
  const summaryGraph = dashboardData?.summary_graph || [];
  const kpi = dashboardData?.kpi || {};
  const salesGraph = dashboardData?.salesGraph || [];
  const topSalesQuality = Array.isArray(dashboardData?.topSalesQuality)
    ? dashboardData.topSalesQuality
    : [];
  const partialTotalCount = kpi?.partialTotalCount;
  const nonPartialTotalCount = kpi?.nonPartialTotalCount;
  const pendingBreakupTooltip = (
    <Box sx={{ whiteSpace: "pre-line" }}>
      {`Partially Processed: ${partialTotalCount} Mtr\nNot Yet Processed: ${nonPartialTotalCount} Mtr`}
    </Box>
  );
  const collectionValue = Number(kpi.collection_last_6_months_lakh || 0);
  const collectionAbs = Math.abs(collectionValue);
  const collectionType = collectionValue < 0 ? "Cr" : "Dr";
  const collectionValueColor = collectionType === "Cr" ? "#2E7D32" : "#C62828";
  const lastUpdatedDate =dashboardData?.last_updated_date ?? "";



  /* ================= UI ================= */
  return (
 <Box
  sx={{
    p: { xs: 1, sm: 3 },      // responsive padding
    background: COLORS.bg,
    minHeight: "100vh",
    mt: isFromApp ? 0 : 6,
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
  }}
>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 1
        }}
      >
        <Box
          sx={{
            background: "#fff",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            px: 1.5,
            py: 0.75,
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 800,
              color: "#d32f2f",
              whiteSpace: "nowrap",
              display: "inline-block",
              minWidth: "max-content",
              animation: "dashboardMarquee 8s linear infinite",
              "@keyframes dashboardMarquee": {
                "0%": {
                  transform: "translateX(100%)"
                },
                "100%": {
                  transform: "translateX(-100%)"
                }
              }
            }}
          >
            UPDATED AS ON: {lastUpdatedDate || "--"}
          </Typography>
        </Box>
      </Box>

      {/* FILTER BAR */}
      {/* ================= FILTER BAR ================= */}
<Paper
  sx={{
    p: 2,
    mb: 2,
    borderRadius: 3,
    mt: 1
  }}
>
  <Grid container spacing={2}>

    {/* ================= ZONE ================= */}
    <Grid item xs={12} sm={6} md={4}>
      <Tooltip title={zoneTooltip} placement="top" arrow>
        <Box width="100%">
          <Select
            options={zoneSelectOptions}
            value={filters.zone}
            onChange={handleZoneChange}
            getOptionValue={(opt) => opt.value}
            getOptionLabel={(opt) => opt.label}
            inputValue={zoneSearchText}
            onInputChange={(val, { action }) => {
              if (action === "input-change") setZoneSearchText(val);
              if (action === "set-value") return zoneSearchText;
              return val;
            }}
            isMulti
            isClearable={Boolean(zoneSearchText)}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            components={{
              Option: CheckboxOption,
              MultiValue: NoMultiValue,
              ClearIndicator: ZoneClearIndicator
            }}
            placeholder={zonePlaceholder}

            /* 🔥 RESPONSIVE FIXES */
            menuPortalTarget={document.body}
            menuPlacement="auto"
            menuPosition="fixed"

            styles={{
              ...selectPortalStyles,
              container: (base) => ({
                ...base,
                width: "100%"
              })
            }}

            onMenuOpen={() => setIsZoneOpen(true)}
            onMenuClose={() => setIsZoneOpen(false)}
          />
        </Box>
      </Tooltip>
    </Grid>

    {/* ================= CUSTOMER ================= */}
    <Grid item xs={12} sm={6} md={5}>
      <Tooltip title={customerTooltip} placement="top" arrow>
        <Box width="100%">
          <Select
            options={customerSelectOptions}
            value={filters.customer}
            onChange={handleCustomerChange}
            getOptionValue={(opt) => opt.value}
            getOptionLabel={(opt) => opt.label}
            inputValue={customerSearchText}
            onInputChange={(val, { action }) => {
              if (action === "input-change") {
                setCustomerSearchText(val);
                if (val.length >= 3) handleCustomerSearch(val);
              }
              if (action === "set-value") return customerSearchText;
              return val;
            }}
            isMulti
            isClearable={Boolean(customerSearchText)}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            components={{
              Option: CheckboxOption,
              MultiValue: NoMultiValue,
              ClearIndicator: CustomerClearIndicator
            }}
            placeholder={customerPlaceholder}

            /* 🔥 RESPONSIVE FIXES */
            menuPortalTarget={document.body}
            menuPlacement="auto"
            menuPosition="fixed"

            styles={{
              ...selectPortalStyles,
              container: (base) => ({
                ...base,
                width: "100%"
              })
            }}

            onMenuOpen={() => setIsCustomerOpen(true)}
            onMenuClose={() => setIsCustomerOpen(false)}
          />
        </Box>
      </Tooltip>
    </Grid>

  </Grid>
</Paper>


      {/* KPI */}
      <Grid container spacing={2} mb={3}>

        <Grid item xs={12} sm={6} md={2.4}>
          <KpiTile
            title="Collection"
            value={`${kpi.collection_last_6_months_lakh || 0} L`}
            // valueColor={collectionValueColor}
            // valueSuffix={collectionType}
            // valueSuffixColor={collectionValueColor}
            subtitle="Current FY"
            color={COLORS.primary}
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            loading={loadingDashboard}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <KpiTile
            title="Net Outstanding"
            value={`${kpi.net_outstanding_current_fy_lakh || 0} L`}
            // subtitle="Current FY"
            color={COLORS.danger}
            icon={<AssessmentIcon fontSize="small" />}
            loading={loadingDashboard}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <KpiTile
            title="Monthly Sales"
            value={`${kpi.sales_orders_current_fy || 0}`}
            subtitle="Current Month"
            color="#2E7D32"     // green premium
            icon={<SellIcon fontSize="small" />}
            loading={loadingDashboard}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <KpiTile
            title="Sales Return"
            value={`${kpi.sales_return_total || 0}`}
            subtitle="Current Month"
            color="#ED6C02"     // amber warning
            icon={<AssignmentReturnIcon fontSize="small" />}
            loading={loadingDashboard}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title={pendingBreakupTooltip} placement="bottom" arrow>
            <Box>
              <KpiTile
                title="Pending Orders"
                value={`${kpi.pending_orders_total || 0}`}
                subtitle="Current FY"
                valueSuffix="Mtr"
                valueColor={COLORS.neutral}
                color={COLORS.neutral}
                icon={<PendingActionsIcon fontSize="small" />}
                loading={loadingDashboard}
              />
            </Box>
          </Tooltip>
        </Grid>

      </Grid>



      {/* CHARTS */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
         
          <ChartCard title="Monthly Sales (Current FY)" loading={loadingDashboard} 
          onViewAll={() => {
            navigate("/current-month-sales-report");
            window.location.reload();
          }}
          >
            <div width="100%" height={300} >
              <MonthlySalesBarLineChart data={salesGraph} height={380} />
            </div>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Top Sales Quality (Mtr)" loading={loadingDashboard}>
            <Box sx={{ pr: 0 }}>
              {topSalesQuality.length ? (
                topSalesQuality.map((row, index) => (
                  <Box
                    key={`${String(row?.material_desc || "").trim()}-${index}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.8,
                      px: 1,
                      mb: 0.6,
                      borderRadius: 1.5,
                      backgroundColor: index % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                      border: "1px solid #E8EDF3"
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: "#23364A", pr: 1 }}
                    >
                      {String(row?.material_desc || "-").replace(/\r/g, "").trim()}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: "#102A43", whiteSpace: "nowrap" }}
                    >
                      {`${row?.amount_lakh || 0}`}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    height: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#667085" }}>
                    No quality data available
                  </Typography>
                </Box>
              )}
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <ChartCard title="Collection Received (Current FY)" loading={loadingDashboard} 
          disabled={false}
          onViewAll={() => {
            navigate("/collection-recieved");
            window.location.reload();
          }}
          >
            <div width="100%" height={300}>
              <CollectionReceivedLineChart collectionData={collectionChart} outstandingData={outstandingChart} />
            </div>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <ChartCard title="Outstanding Aging" loading={loadingDashboard} 
          disabled={false}
        onViewAll={() => {
          navigate("/outstanding-reports");
          window.location.reload();
        }}
          >
            <div width="100%" height={300}>
              <NetAmountLineChart data={summaryGraph} />
            </div>
          </ChartCard>
        </Grid>

        {/* <Grid item xs={12} md={4}>
          <ChartCard title="Schemes Overview" onViewAll={() => navigate("/scheme-report")}>
            <div width="100%" height={300}>
              <SchemesOverviewBarChart />
            </div>
          </ChartCard>
          
        </Grid> */}

        <Grid item xs={12} md={12}>
          <ChartCard title="Pending Orders (Current FY)" loading={loadingDashboard}
          disabled={false}
           onViewAll={() => {
            navigate("/pending-order-reports");
            window.location.reload();
          }}
           >
            <div width="100%" height={300}>
              <PendingOrdersBarChart data={pendingChart} total={kpi.pending_orders_total} />
            </div>
          </ChartCard>
        </Grid>
      </Grid>

      {/* FAB */}
      {/* <Fab
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          backgroundColor: "red",
          "&:hover": { backgroundColor: "red" }
        }}
        onClick={() => navigate("/dashboard")}
      >
        <NavigateNextIcon />
      </Fab> */}
    </Box>

  );
};

export default ReportsDashboard;
